import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminAPI } from '../../../api';

const CellInput = ({ title, type = 'string', onChangeCells, defaultCells = [], checkUserEmail = false }) => {
  const [cells, setCells] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [emailStatus, setEmailStatus] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const verifyingEmails = useRef(new Set());  
  const inputRef = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && defaultCells.length > 0) {
      setCells(defaultCells.map(cell => cell.toLowerCase()));
      initialized.current = true;
    }
  }, [defaultCells]);

  const fetchEmailSuggestions = async (query) => {
    if (!query || query.length <= 2) {
      setSuggestions([]);
      return;
    }
    try {
      const serializedData = JSON.stringify(query);
      const response = await adminAPI.getEmailSuggestions(serializedData);
      setSuggestions(response.users || []);
    } catch (error) {
      console.error('Erro ao buscar sugestões:', error);
      setSuggestions([]);
    }
  };

  useEffect(() => {
    if (!checkUserEmail) return;

    const delayDebounce = setTimeout(() => {
      fetchEmailSuggestions(inputValue);
    }, 300);
  
    return () => clearTimeout(delayDebounce);
  }, [inputValue]);

  const onChangeInput = useCallback(
    (e) => {
      const { value } = e.target;
      const parts = value.split(/[\s,;\r\n]+/).map(p => p.trim().toLowerCase()).filter(Boolean);

      if (parts.length > 1) {
        const last = parts.pop();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const newCells = parts.filter(cell => (type !== 'email' || emailRegex.test(cell)) && !cells.includes(cell));
        setCells([...cells, ...newCells]);
        onChangeCells([...cells, ...newCells]);
        setInputValue(last);
      } else {
        setInputValue(value);
      }
    },
    [cells, type, onChangeCells]
  );

  const onKeyDownInput = useCallback(
    (e) => {
      const trimmedValue = inputValue.trim();

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prevIndex) => (prevIndex + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex <= 0 ? suggestions.length - 1 : prevIndex - 1
        );
      } else if ((e.key === 'Backspace' || e.key === 'Delete') && inputValue === '') {
        if (cells.length > 0) {
          e.preventDefault();
          const updatedCells = [...cells];
          updatedCells.pop();
          setCells(updatedCells);
          onChangeCells(updatedCells);
        }
      } else if ((e.key === 'Enter' || e.key === 'Tab') && highlightedIndex >= 0) {
        e.preventDefault();
        const selected = suggestions[highlightedIndex].email.toLowerCase();
        if (!cells.includes(selected)) {
          setCells([...cells, selected]);
          onChangeCells([...cells, selected]);
        }
        setInputValue('');
        setSuggestions([]);
        setHighlightedIndex(-1);
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      } else if (e.key === 'Enter' && trimmedValue.length > 0) {
        e.preventDefault();
        const lowerCased = trimmedValue.toLowerCase();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (type === 'email' && !emailRegex.test(lowerCased)) {
          return;
        }
        if (!cells.includes(lowerCased)) {
          setCells([...cells, lowerCased]);
          onChangeCells([...cells, lowerCased]);
        }
        setInputValue('');
        setSuggestions([]);
        setHighlightedIndex(-1);
        setTimeout(() => inputRef.current?.focus(), 0);
      }      
    },
    [inputValue, cells, suggestions, highlightedIndex, type, onChangeCells]
  );

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions]);

  const onBlurInput = useCallback(
    (e) => {
      const { value } = e.target;
      if (value.trim().length > 0) {
        const lowerCased = value.trim().toLowerCase();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (type === 'email' && !emailRegex.test(lowerCased)) {
          return;
        }

        setCells((prevCells) => {
          if (prevCells.includes(lowerCased)) return prevCells;
          const updated = [...prevCells, lowerCased];
          onChangeCells(updated);
          return updated;
        });

        setInputValue('');
        setSuggestions([]);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    },
    [type, onChangeCells]
  );

  const onRemoveCell = useCallback((cellToRemove) => {
    setCells((currentCells) => {
      const updated = currentCells.filter((cell) => cell !== cellToRemove);
      onChangeCells(updated);
      return updated;
    });
  }, []);

  const checkUserExistence = async (cell) => {
    const email = cell.toLowerCase();
  
    if (verifyingEmails.current.has(email)) return;
  
    verifyingEmails.current.add(email);
  
    try {
      const serializedData = JSON.stringify(email);
      const response = await adminAPI.userExistEmail(serializedData);
      setEmailStatus((prevStatus) => ({
        ...prevStatus,
        [email]: response.status === 'ok' ? 'ok' : 'nok',
      }));
    } catch (error) {
      console.error('Error checking user:', error);
      setEmailStatus((prevStatus) => ({
        ...prevStatus,
        [email]: 'error',
      }));
    } finally {
      verifyingEmails.current.delete(email);
    }
  };

  useEffect(() => {
    if (!checkUserEmail || cells.length === 0) return;
  
    const uncheckedCells = cells.filter((cell) => (
      !emailStatus[cell] && !verifyingEmails.current.has(cell)
    ));

    uncheckedCells.forEach(checkUserExistence);
  }, [cells, checkUserEmail]);

  const getCellClass = (cell) => {
    if (emailStatus[cell] === 'nok') {
      return 'text-red';
    }
    return '';
  };

  return (
    <div>
      <p className="text-sm uppercase text-charcoal font-bold">{title}</p>
      <div className="email-list w-full p-4 flex flex-wrap border-2 border-gray rounded-lg relative mt-4 gap-2">
        {cells.map((cell, index) => (
          <div
            className={`email-item shadow flex px-1 items-center rounded ${getCellClass(cell)}`}
            key={index}
          >
            {cell}
            <button
              type="button"
              className="ml-2 flex items-center justify-center"
              onClick={() => onRemoveCell(cell)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  stroke="#6357B5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M18 6 6 18M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        ))}

        <input
          className="email-input outline-none bg-transparent border-none"
          type={type}
          ref={inputRef}
          value={inputValue}
          onChange={onChangeInput}
          onBlur={onBlurInput}
          onKeyDown={onKeyDownInput}
        />

        {suggestions.length > 0 && (
          <div className="relative bg-white border border-gray rounded mt-2 w-full z-10 shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className={`px-4 py-2 cursor-pointer ${
                  index === highlightedIndex ? 'bg-purple-500 text-white' : 'hover:bg-purple-200'
                }`}
                onMouseDown={() => {
                  const lowerCased = suggestion.email.toLowerCase();
                  if (!cells.includes(lowerCased)) {
                    setCells([...cells, lowerCased]);
                    onChangeCells([...cells, lowerCased]);
                  }
                  setInputValue('');
                  setSuggestions([]);
                  setHighlightedIndex(-1);
                  setTimeout(() => inputRef.current?.focus(), 0);
                }}
              >
                {suggestion.email}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CellInput;
